import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  MidasOfferPageData,
  MidasProduct,
  IntervalFields,
  RecurringPaymentIntervalUnit,
} from 'types/order-join-flow';
import { getStrapiOrderData } from './fetchStrapiData';
import { useMonitoring } from 'lib/hooks/useMonitoring';
import { getCookie } from 'cookies-next';
import { fetchIntervalUnitAndCount } from './fetchIntervalUnitAndcount';

interface UseFetchStrapiDataResult {
  data: MidasOfferPageData | null;
  error: string | false;
  loading: boolean;
  targetOfferMdc: string;
  eligibleOfferMdc?: string;
  intervals: IntervalFields | null;
}

interface UseFetchStrapiDataProps {
  mdc: string;
  pathname: string;
  selectedProductId?: string;
  termid?: RecurringPaymentIntervalUnit;
  shouldFetch?: boolean;
}

const filterByProductId = (data: MidasOfferPageData, productId: string) => {
  const allProducts = data?.attributes?.midas_products?.data;
  const selectedProduct = allProducts.find(
    (product: MidasProduct) => `${product.id}` === productId,
  );
  if (!selectedProduct) {
    return null;
  }
  return {
    ...data,
    attributes: {
      ...data.attributes,
      midas_products: {
        ...data.attributes.midas_products,
        data: [selectedProduct],
      },
    },
  };
};

export const filterByTermId = (
  data: MidasOfferPageData,
  termid?: RecurringPaymentIntervalUnit,
): MidasOfferPageData => {
  const allProducts = data?.attributes?.midas_products?.data ?? [];

  if (allProducts.length <= 1) return data;
  if (!termid) return data;

  const matchingProduct = allProducts.find((product) => {
    const { recurring_payment_interval_unit, AtkInternalIdentifier } =
      product.attributes;

    if (recurring_payment_interval_unit) {
      return recurring_payment_interval_unit === termid;
    }

    const termPattern =
      termid === RecurringPaymentIntervalUnit.Year
        ? /annual|year/i
        : termid === RecurringPaymentIntervalUnit.Month
        ? /monthly|month/i
        : null;

    if (!termPattern) return false;

    return termPattern.test(AtkInternalIdentifier);
  });

  if (!matchingProduct) return data;

  return {
    ...data,
    attributes: {
      ...data.attributes,
      midas_products: {
        ...data.attributes.midas_products,
        data: [matchingProduct],
      },
    },
  };
};

export const useFetchStrapiData = ({
  mdc,
  pathname,
  selectedProductId,
  termid,
}: UseFetchStrapiDataProps): UseFetchStrapiDataResult => {
  const userToken = getCookie('user_token');
  const [data, setData] = useState<MidasOfferPageData | null>(null);
  const [error, setError] = useState<string | false>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [targetOfferMdc, setTargetOfferMdc] = useState<string>(() => mdc);
  const [eligibleOfferMdc, setEligibleOfferMdc] = useState<string>();
  const [intervals, setIntervals] = useState<IntervalFields | null>(null);
  // TODO: this log function was causing a memory leak
  const { log } = useMonitoring();
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(false);

    const fetchData = async (): Promise<void> => {
      try {
        const data = await getStrapiOrderData({ mdc, userToken });
        // No offer available for user
        if (!data) return;

        if (isMounted) {
          let midasProductData = data;
          const termFilteredProduct = filterByTermId(midasProductData, termid);
          midasProductData = termFilteredProduct;
          if (selectedProductId) {
            const filteredData = filterByProductId(
              midasProductData,
              selectedProductId,
            );
            if (!filteredData) {
              log('ERROR', `Product not found: ${selectedProductId}`);
              throw new Error('Product not found');
            }
            midasProductData = filteredData;
          }
          const promoCode =
            midasProductData.attributes.midas_products.data[0].attributes
              .promotionCode;

          if (!promoCode)
            throw new Error(`No promotion code provided => ${promoCode}`);

          const midasProduct = await fetchIntervalUnitAndCount(promoCode);

          if (midasProduct) {
            setIntervals({
              recurring_payment_interval_unit:
                midasProduct[0].attributes.recurring_payment_interval_unit,
              recurring_payment_interval_count:
                midasProduct[0].attributes.recurring_payment_interval_count,
            });
          }
          setData(midasProductData);
          setLoading(false);
        }
      } catch (error: unknown) {
        if (!isMounted) return;
        const e = error as Error;
        log('ERROR', `${e}, Path: ${pathname}`);
        setError(e.message);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [log, mdc, pathname, selectedProductId, termid, userToken]);

  useEffect(() => {
    if (!data) return;

    setEligibleOfferMdc(data.attributes?.mdc_code);

    // if the targetOfferMdc is undefined, use the eligible offer to set it
    //...this is when it isn't present as a query param
    setTargetOfferMdc((prevState) => {
      if (!prevState || prevState !== data.attributes?.mdc_code) {
        return data.attributes?.mdc_code;
      }
      return prevState;
    });
  }, [data]);

  return {
    data,
    error,
    loading,
    targetOfferMdc,
    eligibleOfferMdc,
    intervals,
  };
};

export const useEligibleOfferRedirectHandler = ({
  targetOfferMdc,
  eligibleOfferMdc,
}: Pick<UseFetchStrapiDataResult, 'targetOfferMdc' | 'eligibleOfferMdc'>) => {
  const router = useRouter();
  const { log } = useMonitoring();
  useEffect(() => {
    if (!eligibleOfferMdc) return;
    if (targetOfferMdc !== eligibleOfferMdc) {
      log('INFO', `${targetOfferMdc} doesn't match ${eligibleOfferMdc}.`);

      // TODO, can we push the user to the checkout page here instead of reloading the page
      //...with VWO
      router.push({
        pathname: '/order/checkout',
        query: { ...router.query, mdc: eligibleOfferMdc },
      });
    }
  }, [eligibleOfferMdc, log, router, targetOfferMdc]);
};
